import React, { useState, useEffect, useCallback, useRef } from "react";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import "./priceRangeSlider.styles.scss";

const PriceRangeSlider = ({
  products = [],
  onPriceChange,
  resetTrigger = 0,
}) => {
  const [minPrice, setMinPrice] = useState(0);
  const [maxPrice, setMaxPrice] = useState(100000);
  const [range, setRange] = useState([0, 100000]);
  const [isInitialized, setIsInitialized] = useState(false);
  const hasInitialized = useRef(false);
  const onPriceChangeRef = useRef(onPriceChange);

  // Calculate price range from products
  useEffect(() => {
    if (products && products.length > 0) {
      const prices = products
        .map((product) => {
          const price = product.precio || product.price || 0;
          return typeof price === "string"
            ? parseFloat(price.replace(/[^0-9.-]+/g, ""))
            : parseFloat(price);
        })
        .filter((price) => !isNaN(price) && price > 0);

      if (prices.length > 0) {
        let min = Math.floor(Math.min(...prices));
        let max = Math.ceil(Math.max(...prices));

        // Handle edge case where min and max are the same
        if (min === max) {
          // If there's only one price, create a range around it
          if (min === 0) {
            min = 0;
            max = 1000; // Set a reasonable max when price is 0
          } else {
            min = Math.max(1, Math.floor(min * 0.9)); // 10% below the price
            max = Math.ceil(max * 1.1); // 10% above the price
          }
        }

        setMinPrice(min);
        setMaxPrice(max);
        setRange([min, max]);
        setIsInitialized(true);

        // Initialize parent's price range only on first load
        if (onPriceChangeRef.current && !hasInitialized.current) {
          onPriceChangeRef.current({ min, max });
          hasInitialized.current = true;
        }
      } else {
        // Fallback values if no valid prices found
        setMinPrice(0);
        setMaxPrice(100000);
        setRange([0, 100000]);
        setIsInitialized(true);

        // Initialize parent's price range only on first load
        if (onPriceChangeRef.current && !hasInitialized.current) {
          onPriceChangeRef.current({ min: 0, max: 100000 });
          hasInitialized.current = true;
        }
      }
    } else {
      // No products array provided
      setMinPrice(0);
      setMaxPrice(100000);
      setRange([0, 100000]);
      setIsInitialized(true);

      // Initialize parent's price range only on first load
      if (onPriceChangeRef.current && !hasInitialized.current) {
        onPriceChangeRef.current({ min: 0, max: 100000 });
        hasInitialized.current = true;
      }
    }
  }, [products]); // Removed onPriceChange from dependencies

  // Update the ref when onPriceChange changes
  useEffect(() => {
    onPriceChangeRef.current = onPriceChange;
  }, [onPriceChange]);

  // Reset when resetTrigger changes
  useEffect(() => {
    if (isInitialized) {
      setRange([minPrice, maxPrice]);
      // Also reset parent's price range
      if (onPriceChangeRef.current) {
        onPriceChangeRef.current({ min: minPrice, max: maxPrice });
      }
      // Reset initialization flag
      hasInitialized.current = false;
    }
  }, [resetTrigger, minPrice, maxPrice, isInitialized]); // Removed onPriceChange from dependencies

  // Debounced price change handler
  const debouncedOnPriceChange = useCallback(
    debounce((newRange) => {
      if (onPriceChangeRef.current) {
        onPriceChangeRef.current({ min: newRange[0], max: newRange[1] });
      }
    }, 300),
    [] // Removed onPriceChange from dependencies
  );

  // Notify parent component of price changes
  useEffect(() => {
    if (isInitialized) {
      debouncedOnPriceChange(range);
    }
  }, [range, isInitialized]); // Removed debouncedOnPriceChange from dependencies

  const handleSliderChange = (newRange) => {
    setRange(newRange);
  };

  const handleInputChange = (index, value) => {
    const numValue = parseInt(value) || (index === 0 ? minPrice : maxPrice);
    const newRange = [...range];

    if (index === 0) {
      newRange[0] = Math.max(minPrice, Math.min(numValue, newRange[1] - 1));
    } else {
      newRange[1] = Math.min(maxPrice, Math.max(numValue, newRange[0] + 1));
    }

    setRange(newRange);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
      minimumFractionDigits: 0,
    }).format(price);
  };

  if (!isInitialized) {
    return (
      <div className="price-range-slider">
        <div className="price-range-slider__header">
          <p className="filters__filter__name">Precio</p>
        </div>
        <div className="price-range-slider__loading">
          <p>Cargando filtro de precios...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="price-range-slider">
      <div className="price-range-slider__header">
        <p className="filters__filter__name">Precio</p>
      </div>

      <div className="price-range-slider__content">
        {/* Price inputs */}
        <div className="price-range-slider__inputs">
          <div className="price-input-group">
            <label htmlFor="min-price">Mínimo</label>
            <input
              id="min-price"
              type="number"
              value={range[0]}
              min={minPrice}
              max={range[1] - 1}
              onChange={(e) => handleInputChange(0, e.target.value)}
              className="price-input"
            />
          </div>
          <div className="price-input-separator">-</div>
          <div className="price-input-group">
            <label htmlFor="max-price">Máximo</label>
            <input
              id="max-price"
              type="number"
              value={range[1]}
              min={range[0] + 1}
              max={maxPrice}
              onChange={(e) => handleInputChange(1, e.target.value)}
              className="price-input"
            />
          </div>
        </div>

        {/* RC Slider */}
        <div className="price-range-slider__slider-container">
          <Slider
            range
            min={minPrice}
            max={maxPrice}
            value={range}
            onChange={handleSliderChange}
            className="price-range-slider__slider"
            trackStyle={[{ backgroundColor: "#e10009", height: 6 }]}
            handleStyle={[
              {
                borderColor: "#e10009",
                height: 20,
                width: 20,
                marginTop: -7,
                backgroundColor: "#e10009",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
              },
              {
                borderColor: "#e10009",
                height: 20,
                width: 20,
                marginTop: -7,
                backgroundColor: "#e10009",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
              },
            ]}
            railStyle={{ backgroundColor: "#ddd", height: 6 }}
            dotStyle={{
              borderColor: "#e10009",
              bottom: -2,
            }}
            activeDotStyle={{
              borderColor: "#e10009",
            }}
          />
        </div>

        {/* Price display */}
        <div className="price-range-slider__display">
          <span className="price-display">
            {formatPrice(range[0])} - {formatPrice(range[1])}
          </span>
        </div>

        {/* Range info */}
        <div className="price-range-slider__info">
          <small>
            Rango disponible: {formatPrice(minPrice)} - {formatPrice(maxPrice)}
          </small>
        </div>
      </div>
    </div>
  );
};

// Simple debounce function
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export default PriceRangeSlider;
